import { Button, Flex, Form, Typography } from 'antd'
import { InputOTP } from 'antd-input-otp'
import { useTheme } from 'antd-style'
import cn from 'classnames'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useLoginServiceTwoFactorLoginEndpoint,
  useLoginServiceTwoFactorResendTokenEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import {
  requiredRules,
  USER_ID_KEY,
  USER_INSTITUTION_KEY
} from 'src/app-constants'
import { message, Text } from 'src/components'
import { useSessionContext } from 'src/contexts'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, parseToken } from 'src/utils'

import { AuthHeader } from '../components'
import { useStyles } from '../styles'

const { Link } = Typography

export const OtpConfirmationPage = () => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const navigate = useNavigate()
  const { onLogin } = useSessionContext()
  const palette = useTheme()

  const [countdown, setCountdown] = useState(60)

  const [form] = Form.useForm()

  const { mutate: loginMutate } = useLoginServiceTwoFactorLoginEndpoint({
    onError: (e: ApiError) =>
      e.body.errors
        ? formErrorHandler(e.body.errors)
        : message.error(t('auth.wrong_otp')),
    onSuccess: (data) => onLogin(data)
  })

  const { mutate: resendTokenMutate } =
    useLoginServiceTwoFactorResendTokenEndpoint()

  useEffect(() => {
    let intervalId: NodeJS.Timeout

    if (countdown > 0) {
      intervalId = setInterval(() => {
        setCountdown((prevCount) => prevCount - 1)
      }, 1000)
    }

    return () => clearInterval(intervalId)
  }, [countdown])

  const onSubmit = async () => {
    const values = await form.validateFields()

    loginMutate({
      requestBody: {
        code: values.code.join(''),
        legalEntityId:
          parseToken(localStorage.getItem(USER_INSTITUTION_KEY)) ?? undefined,
        roleId: parseToken(localStorage.getItem(USER_ID_KEY)) ?? undefined
      }
    })
  }

  const onClick = () => {
    navigate(paths.auth.root)
  }

  const onResendClick = () => {
    if (countdown === 0) {
      setCountdown(60)
      resendTokenMutate({
        requestBody: {
          twoFactorProvider: 'Email'
        }
      })
    }
  }

  return (
    <Flex vertical gap={32}>
      <AuthHeader
        title={t('auth.login_title')}
        description={t('auth.confirm_otp_subtitle')}
      />

      <Form layout="vertical" form={form}>
        <Form.Item
          name="code"
          rules={requiredRules}
          label={t('auth.verification_code')}
        >
          <InputOTP
            type="number"
            min={0}
            max={9}
            wrapperClassName={styles.otpWrapper}
            inputClassName={cn(styles.otpInput, 'input-number-without-arrow')}
          />
        </Form.Item>
      </Form>

      <Flex vertical align="center">
        <Link onClick={onResendClick}>
          <Button type="link" style={{ padding: 0 }} disabled={countdown > 0}>
            <Text color={palette.grey.grey2} weight={700}>
              {t('auth.resend_code')} {countdown > 0 && `(${countdown})`}
            </Text>
          </Button>
        </Link>
      </Flex>

      <Flex vertical gap={16}>
        <Button type="primary" size="large" onClick={onSubmit}>
          {t('common.confirm')}
        </Button>

        <Button type="link" className={styles.linkButton} onClick={onClick}>
          <Text>{t('auth.return_to_auth')}</Text>
        </Button>
      </Flex>
    </Flex>
  )
}
